import { Button, Text, Group, Box, Stack, Anchor } from "@mantine/core";
import {
  IconArrowBarToLeft,
  IconArrowLeft,
  IconArrowRight,
  IconArrowBarToRight,
} from "@tabler/icons-react";

interface PaginationControlsProps {
  currentPage: number;
  totalPages: number;
  totalListings: number;
  lastUpdated: Date | null;
  onPageChange: (page: number) => void;
  onPrivacyClick: () => void;
}

export const PaginationControls = ({
  currentPage,
  totalPages,
  totalListings,
  lastUpdated,
  onPageChange,
  onPrivacyClick,
}: PaginationControlsProps) => {
  const getUpdateText = () => {
    if (totalListings === 0) return "No data yet";

    let text = `${totalListings} listings`;

    if (lastUpdated) {
      const year = lastUpdated.getFullYear();
      const month = (lastUpdated.getMonth() + 1).toString().padStart(2, "0");
      const day = lastUpdated.getDate().toString().padStart(2, "0");
      const hours = lastUpdated.getHours().toString().padStart(2, "0");
      const minutes = lastUpdated.getMinutes().toString().padStart(2, "0");
      text += `, last updated ${year}-${month}-${day} ${hours}:${minutes}`;
    }

    return text;
  };

  return (
    <>
      {/* Desktop Layout */}
      <Stack gap="md" mt="md" className="desktop-pagination">
        {totalPages > 1 && (
          <Group justify="center">
            <Button
              variant="light"
              color="nvidiaGreen"
              size="xs"
              onClick={() => onPageChange(1)}
              disabled={currentPage === 1}
            >
              <IconArrowBarToLeft size={16} />
            </Button>
            <Button
              variant="light"
              color="nvidiaGreen"
              size="xs"
              onClick={() => onPageChange(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
            >
              <IconArrowLeft size={16} />
            </Button>
            <Text c="dimmed" size="md">
              {currentPage} of {totalPages}
            </Text>
            <Button
              variant="light"
              color="nvidiaGreen"
              size="xs"
              onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
            >
              <IconArrowRight size={16} />
            </Button>
            <Button
              variant="light"
              color="nvidiaGreen"
              size="xs"
              onClick={() => onPageChange(totalPages)}
              disabled={currentPage === totalPages}
            >
              <IconArrowBarToRight size={16} />
            </Button>
          </Group>
        )}
        <Text size="xs" c="dimmed" ta="center" style={{ fontFamily: "monospace" }}>
          {getUpdateText()}
        </Text>
        <Text size="xs" c="dimmed" ta="center">
          <Anchor component="button" size="xs" c="dimmed" onClick={onPrivacyClick} style={{ textDecoration: "underline" }}>
            Privacy Policy
          </Anchor>
        </Text>
      </Stack>

      {/* Mobile Layout */}
      <Box mt="md" className="mobile-pagination">
        {totalPages > 1 && (
          <Group justify="center">
            <Button
              variant="light"
              color="nvidiaGreen"
              size="xs"
              onClick={() => onPageChange(1)}
              disabled={currentPage === 1}
            >
              <IconArrowBarToLeft size={16} />
            </Button>
            <Button
              variant="light"
              color="nvidiaGreen"
              size="xs"
              onClick={() => onPageChange(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
            >
              <IconArrowLeft size={16} />
            </Button>
            <Text c="dimmed" size="md">
              {currentPage} of {totalPages}
            </Text>
            <Button
              variant="light"
              color="nvidiaGreen"
              size="xs"
              onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
            >
              <IconArrowRight size={16} />
            </Button>
            <Button
              variant="light"
              color="nvidiaGreen"
              size="xs"
              onClick={() => onPageChange(totalPages)}
              disabled={currentPage === totalPages}
            >
              <IconArrowBarToRight size={16} />
            </Button>
          </Group>
        )}
        <Text size="xs" c="dimmed" ta="center" mt="xl" style={{ fontFamily: "monospace" }}>
          {getUpdateText()}
        </Text>
        <Text size="xs" c="dimmed" ta="center" mt="xs">
          <Anchor component="button" size="xs" c="dimmed" onClick={onPrivacyClick} style={{ textDecoration: "underline" }}>
            Privacy Policy
          </Anchor>
        </Text>
      </Box>
    </>
  );
};
